import React from 'react';
import { View, TextInput, Pressable } from '../../web/react-native';
import Icon from '../../components/Icon';
import styled from '../../components/styled';
import { useDispatch } from 'react-redux';

import {
  selectAllBusinessTeams,
  selectAllConsoles,
  selectAllPosts,
  selectAllSites,
  selectPreferences,
  preferenceActions,
} from '../../store';
import { AssetType } from '../../services/dataModels';
import { AppDispatch } from '../../store/store';

const AssetTypePicker = () => {
  const dispatch = useDispatch<AppDispatch>();

  const preferences = selectPreferences.useHook();
  const businessTeams = selectAllBusinessTeams.useHook();
  const consoles = selectAllConsoles.useHook();
  const posts = selectAllPosts.useHook();
  const sites = selectAllSites.useHook();

  const settings: Record<
    AssetType,
    {
      isPickerDisabled: boolean;
      placeholder: string;
      preference: string;
    }
  > = {
    'Business Team': {
      isPickerDisabled:
        preferences?.site === undefined || Object.entries(businessTeams).length === 0,
      placeholder:
        preferences?.site && Object.entries(businessTeams).length === 0
          ? 'No Plants available'
          : 'Select Plant',
      preference: preferences?.businessTeam?.displayName,
    },
    Console: {
      isPickerDisabled:
        preferences?.businessTeam === undefined || Object.entries(console).length === 0,
      placeholder:
        preferences?.businessTeam && Object.entries(consoles).length === 0
          ? 'No Consoles available'
          : `Select ${AssetType.Console}`,
      preference: preferences?.console?.displayName,
    },
    Post: {
      isPickerDisabled: preferences?.console === undefined || Object.entries(posts).length === 0,
      placeholder:
        preferences?.console && Object.entries(posts).length === 0
          ? 'No Posts available'
          : `Select ${AssetType.Post}`,
      preference: preferences?.post?.displayName,
    },
    Site: {
      isPickerDisabled: sites === undefined || Object.entries(sites).length === 0,
      placeholder:
        sites !== undefined && Object.entries(sites).length !== 0
          ? `Select ${AssetType.Site}`
          : 'No Sites please refresh',
      preference: preferences?.site?.displayName,
    },
  };

  const handleAssetTypeSelected = (assetType) => {
    dispatch(preferenceActions.assetTypeSelected(assetType));
  };

  return (
    <View style={{ flex: 1 }}>
      <View>
        <SetPreferencesText>Set your preferences</SetPreferencesText>
        <SetPreferencesInvitationText>
          You can change them later in settings
        </SetPreferencesInvitationText>
      </View>

      <PickersContainer>        
        {Object.values(AssetType).map((type, index) => (
          <AssetPickerContainer key={index}>            
            <AssetTitle disabled={settings[type].isPickerDisabled}>{type}</AssetTitle>
            <AssetSelectorBox
              onPress={() => {
                handleAssetTypeSelected(type);
              }}
              disabled={settings[type].isPickerDisabled}>
              <InputRow pointerEvents="none">
                <AssetTextField
                  placeholder={settings[type].placeholder}
                  value={settings[type].preference ?? ''}
                />
                <Triangle disabled={settings[type].isPickerDisabled} />
              </InputRow>
            </AssetSelectorBox>
          </AssetPickerContainer>
        ))}
      </PickersContainer>
    </View>
  );
};

export default AssetTypePicker;

const InputRow = styled.View`
  flex-direction: row !important;
  align-items: center;
`;

const AssetTextField = styled(TextInput).attrs({
  placeholderTextColor: '#ccc',
  editable: false,
})`
  flex: 1;
  height: 40px;
  color: #222;
`;

const AssetPickerContainer = styled.View``;

const AssetTitle = styled.Text<DisabledProps>`
  color: ${(props) => {
    return props.disabled ? '#ccc' : '#222';
  }};
`;

const PickersContainer = styled.View`
  padding-top: 20px;
`;

interface DisabledProps {
  disabled: boolean;
}

const AssetSelectorBox = styled(Pressable)<DisabledProps>`
  height: 40px;
  border-radius: 4px;
  border-width: 1px;
  border-color: #ccc;
  background-color: ${(props) => {
    return props.disabled ? '#ffffff77' : '#fff';
  }};
  margin-top: 3px;
  margin-bottom: 10px;
  padding-left: 8px;
`;

const SetPreferencesText = styled.Text`
  color: #222;
  font-size: 18px;
  font-weight: bold;
`;

const SetPreferencesInvitationText = styled.Text`
  color: #222;
  font-size: 12px;
`;

const Triangle = styled(Icon).attrs({
  name: 'arrow-drop-down',
})<DisabledProps>`
  font-size: 24px;
  color: ${(props) => {
    return props.disabled ? '#ccc' : '#777';
  }};
`;
